import React from "react";
import { Divider, Select, Spin, Input, InputNumber } from "antd";
import { RefundType, getPaymentMethodCode, PaymentMethod } from "../../Enums";
import {
  PaymentOption,
  PaymentOptionValue,
} from "../PaymentsOption/PaymentOption";

const LT = 0.01;

export const isPaymentComplete = (
  method: any,
  pay: PaymentOptionValue,
  expectedAmount: number,
): boolean => {
  if (!method || expectedAmount <= 0) return false;
  const amount = Number(pay.amount ?? 0);
  if (Math.abs(amount - expectedAmount) > LT) return false;

  const code = String(method.code || "").toLowerCase();
  if (code === getPaymentMethodCode(PaymentMethod.CASH)) return true;
  if (code === getPaymentMethodCode(PaymentMethod.CARD))
    return Boolean(pay.bank_account_id);
  if (code === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER))
    return Boolean(pay.bank_account_id);
  if (code === getPaymentMethodCode(PaymentMethod.CHEQUE))
    return Boolean(pay.cheque_no && pay.bank_name);
  return false;
};

type Props = {
  orderDetails: any;
  loadingOrderDetails: boolean;
  refundTypeId: string | null;
  setRefundTypeId: (v: string) => void;
  refundTypes: any[];
  returnItems: any[];
  returnTypes: any[];
  handleReturnQtyChange: (index: number, value: number) => void;
  handleReturnTypeChange: (index: number, value: string) => void;
  itemsTotalRefund: number;
  refundable: number;
  dueAmount: number;
  paymentMethods: any[];
  bankAccounts: any[];
  refundMethodId: string | null;
  setRefundMethodId: (v: string) => void;
  refundPayment: PaymentOptionValue;
  setRefundPayment: (v: PaymentOptionValue) => void;
  note: string;
  setNote: (v: string) => void;
  cashBalance: number;
  setCashBalance: (v: number) => void;
};

export const OrderReturnForm: React.FC<Props> = ({
  orderDetails,
  loadingOrderDetails,
  refundTypeId,
  setRefundTypeId,
  refundTypes,
  returnItems,
  returnTypes,
  handleReturnQtyChange,
  handleReturnTypeChange,
  itemsTotalRefund,
  refundable,
  dueAmount,
  paymentMethods,
  bankAccounts,
  refundMethodId,
  setRefundMethodId,
  refundPayment,
  setRefundPayment,
  note,
  setNote,
  cashBalance,
  setCashBalance,
}) => {
  const refundPm = paymentMethods.find((m: any) => m.id === refundMethodId);

  const needsRefundPayment =
    refundable > 0 && refundTypeId === RefundType.CASH_REF;

  const isStoreCredit = refundTypeId === RefundType.STORE_CREDIT;
  const storeCreditAmount = Math.max(
    0,
    refundable - Math.max(0, Math.min(cashBalance, refundable)),
  );

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* INVOICE SUMMARY */}
        <div className="border border-gray-100 rounded-xl p-4 bg-white">
          <div className="flex justify-between items-center mb-2">
            <div>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                Invoice
              </div>
              <div className="text-base font-extrabold text-gray-800">
                {orderDetails?.order_no}
              </div>
            </div>

            <div className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded">
              {orderDetails?.order_date}
            </div>
          </div>

          <Divider className="my-3 border-gray-100" />

          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="text-left">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Customer
              </div>
              <div className="text-xs font-semibold text-gray-700 truncate">
                {orderDetails?.customer_name || "Walk-in"}
              </div>
            </div>

            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Total
              </div>
              <div className="text-xs font-bold text-gray-800">
                LKR {orderDetails?.grand_total?.toLocaleString() || "0.00"}
              </div>
            </div>

            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Paid
              </div>
              <div className="text-xs font-bold text-emerald-600">
                LKR {orderDetails?.paid_amount?.toLocaleString() || "0.00"}
              </div>
            </div>

            <div className="text-right">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Due
              </div>
              <div className="text-xs font-bold text-red-500">
                LKR {orderDetails?.due_amount?.toLocaleString() || "0.00"}
              </div>
            </div>
          </div>
        </div>

        {/* RETURN INFO */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="mb-2 text-xs font-bold text-gray-500 uppercase tracking-wide">
            Return Information
          </div>

          <Select
            value={refundTypeId || undefined}
            onChange={setRefundTypeId}
            options={refundTypes?.map((b: any) => ({
              value: b.id,
              label: b.name,
            }))}
            placeholder={
              refundable > 0 ? "Select Refund Type" : "No refund needed"
            }
            className="w-full h-9 text-xs"
          />

          {refundTypeId === RefundType.ITEM_EXCHANGE && refundable > 0 && (
            <div className="mt-3 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
              <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">
                Item Exchange Credit
              </div>
              <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
                <strong>LKR {refundable.toLocaleString()}</strong> will be
                applied as an exchange offset (−) on the next sale. Close this
                drawer, add any products through the normal POS flow, and the
                return amount is deducted automatically at checkout.
              </p>
            </div>
          )}

          {isStoreCredit && refundable > 0 && (
            <div className="mt-3 bg-indigo-50/60 border border-indigo-100 rounded-lg p-3">
              <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wide">
                Store Credit Split- LKR {refundable.toLocaleString()}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider">
                    Cash Balance (paid from our side)
                  </span>
                  <InputNumber
                    min={0}
                    max={refundable}
                    value={cashBalance}
                    onChange={(v) => setCashBalance(Math.max(0, v || 0))}
                    placeholder="0.00"
                    className="w-full rounded"
                    size="small"
                  />
                  <span className="text-[9px] text-indigo-300">
                    Deducted (−) from the customer's next bill.
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider">
                    Store Credit Value
                  </span>
                  <div className="h-6 flex items-center text-xs font-extrabold text-indigo-700">
                    LKR {storeCreditAmount.toLocaleString()}
                  </div>
                  <span className="text-[9px] text-indigo-300">
                    Issued as a Customer Credit document (balance + reason).
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ITEMS */}
        <div className="border border-gray-100 rounded-xl p-4 bg-white">
          <div className="mb-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
            Return Items
          </div>

          {loadingOrderDetails ? (
            <div className="text-center py-8">
              <Spin size="small" />
            </div>
          ) : returnItems.length === 0 ? (
            <div className="text-center text-xs text-gray-400 py-4 italic">
              No returnable items.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {returnItems.map((item, index) => (
                <div
                  key={item.order_item_id}
                  className="flex flex-col gap-2.5 py-3 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-gray-800 truncate">
                        {item.product_name}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5 flex flex-wrap gap-x-2 gap-y-1">
                        <span>
                          Batch:{" "}
                          <strong className="text-gray-600">
                            {item.batch_no || "-"}
                          </strong>
                        </span>
                        <span>•</span>
                        <span>
                          Unit:{" "}
                          <strong className="text-gray-600">
                            {item.unit || "-"}
                          </strong>
                        </span>
                        <span>•</span>
                        <span>
                          Price:{" "}
                          <strong className="text-gray-600">
                            LKR {item.price}
                          </strong>
                        </span>
                      </div>
                    </div>
                    {item.max_quantity > 0 && (
                      <span className="text-[9px] font-bold text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-100 shrink-0">
                        Max: {item.max_quantity}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                        Qty
                      </span>
                      <InputNumber
                        min={0}
                        max={item.max_quantity}
                        value={item.quantity}
                        onChange={(v) => handleReturnQtyChange(index, v || 0)}
                        size="small"
                        className="w-full rounded"
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                        Condition
                      </span>
                      <Select
                        size="small"
                        className="w-full"
                        value={item.return_type_id}
                        onChange={(v) => handleReturnTypeChange(index, v)}
                        options={returnTypes.map((t: any) => ({
                          value: t.id,
                          label: t.name,
                        }))}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                        Stock Action
                      </span>
                      <span className="text-[10px] text-gray-500 h-6 flex items-center">
                        {item.return_type_id === "good"
                          ? "Restore"
                          : "Move to condition"}
                      </span>
                    </div>

                    <div className="flex flex-col gap-0.5 text-right sm:text-right">
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                        Refund
                      </span>
                      <span className="text-xs font-bold text-indigo-600 h-6 flex items-center justify-end">
                        LKR {item.total_refund?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Divider className="my-4 border-gray-100" />

          {/* SUMMARY */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50/50 p-3 rounded-lg border border-gray-100">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Return Value
              </div>
              <strong className="text-sm font-extrabold text-gray-800">
                LKR {itemsTotalRefund.toLocaleString()}
              </strong>
            </div>

            <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
              <div className="text-[10px] text-blue-500 font-bold uppercase tracking-wider">
                Refundable{" "}
                {dueAmount > 0
                  ? `(after due ${Number(dueAmount).toLocaleString()})`
                  : ""}
              </div>
              <strong className="text-sm font-extrabold text-blue-800">
                LKR {refundable.toLocaleString()}
              </strong>
            </div>
          </div>

          {/* REFUND PAYMENT */}
          {needsRefundPayment && (
            <>
              <Divider className="my-4 border-gray-100" />
              <div className="mb-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Refund To Customer- LKR {refundable.toLocaleString()}
              </div>
              <div className="flex flex-col gap-3">
                <Select
                  value={refundMethodId || undefined}
                  onChange={setRefundMethodId}
                  placeholder="Refund payment method"
                  options={paymentMethods.map((m: any) => ({
                    value: m.id,
                    label: m.name,
                  }))}
                  className="w-full h-9 text-xs"
                />
                {refundPm && (
                  <PaymentOption
                    paymentMethodCode={refundPm.code}
                    value={refundPayment}
                    onChange={setRefundPayment}
                    bankAccounts={bankAccounts}
                  />
                )}
              </div>
            </>
          )}

          <div className="mt-4">
            <Input.TextArea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional return notes..."
              className="rounded-lg text-xs"
            />
          </div>
        </div>
      </div>
    </>
  );
};
